import React, { useState, useEffect } from 'react'
import { Card, Button, Input } from '../ui'
import { Search, Users, Music, Clock, ChevronRight, Activity, Trash2, RefreshCw, Layers } from 'lucide-react'
import { cn } from '../../lib/utils'
import toast from 'react-hot-toast'

const AdminRoomsTab = ({
    rooms,
    loadingRooms,
    fetchRooms,
    inspectRoom,
    selectedRoom,
    roomQueue,
    loadingQueue,
    formatDate
}) => {
    const [roomSearch, setRoomSearch] = useState('')
    const [sortBy, setSortBy] = useState('activity') // 'activity', 'songs', 'code'

    const filteredRooms = rooms.filter(room =>
        room.code.toLowerCase().includes(roomSearch.toLowerCase())
    ).sort((a, b) => {
        if (sortBy === 'activity') return new Date(b.lastActivity) - new Date(a.lastActivity)
        if (sortBy === 'songs') return b.songCount - a.songCount
        if (sortBy === 'code') return a.code.localeCompare(b.code)
        return 0
    })

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Rooms List */}
                <div className={cn("space-y-4", selectedRoom ? "lg:col-span-1" : "lg:col-span-3")}>
                    <div className="flex items-center justify-between gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                            <Input
                                placeholder="Search rooms..."
                                value={roomSearch}
                                onChange={(e) => setRoomSearch(e.target.value)}
                                className="pl-10 bg-white/5 border-white/10 text-white"
                            />
                        </div>
                        <Button
                            variant="glass"
                            size="icon"
                            onClick={fetchRooms}
                            disabled={loadingRooms}
                            className="flex-shrink-0"
                        >
                            <RefreshCw className={cn("h-4 w-4", loadingRooms && "animate-spin")} />
                        </Button>
                    </div>

                    <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
                        <span className="text-xs font-black uppercase tracking-widest text-slate-500 mr-2">Sort:</span>
                        <Button
                            variant={sortBy === 'activity' ? 'glass' : 'ghost'}
                            size="sm"
                            onClick={() => setSortBy('activity')}
                            className="text-[10px] uppercase font-bold tracking-tighter"
                        >
                            Recent Activity
                        </Button>
                        <Button
                            variant={sortBy === 'songs' ? 'glass' : 'ghost'}
                            size="sm"
                            onClick={() => setSortBy('songs')}
                            className="text-[10px] uppercase font-bold tracking-tighter"
                        >
                            Song Count
                        </Button>
                        <Button
                            variant={sortBy === 'code' ? 'glass' : 'ghost'}
                            size="sm"
                            onClick={() => setSortBy('code')}
                            className="text-[10px] uppercase font-bold tracking-tighter"
                        >
                            Room Code
                        </Button>
                    </div>

                    <div className="space-y-3">
                        {loadingRooms ? (
                            <div className="py-20 text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4" />
                                <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">Loading Rooms...</p>
                            </div>
                        ) : filteredRooms.length === 0 ? (
                            <div className="py-20 text-center bg-white/5 rounded-3xl border border-white/10 border-dashed">
                                <Users className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                                <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">No Rooms Active</p>
                            </div>
                        ) : (
                            filteredRooms.map(room => (
                                <button
                                    key={room.id}
                                    onClick={() => inspectRoom(room)}
                                    className={cn(
                                        "w-full text-left p-4 rounded-2xl border transition-all duration-300 group relative overflow-hidden",
                                        selectedRoom?.id === room.id
                                            ? "bg-primary-500/10 border-primary-500/50 shadow-lg shadow-primary-500/10"
                                            : "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                                    )}
                                >
                                    <div className="flex items-center justify-between relative z-10">
                                        <div className="flex items-center gap-4">
                                            <div className={cn(
                                                "w-12 h-12 rounded-xl flex items-center justify-center font-black text-lg transition-transform group-hover:scale-110",
                                                selectedRoom?.id === room.id ? "bg-primary-500 text-white" : "bg-black/30 text-primary-400"
                                            )}>
                                                {room.code.slice(0, 2)}
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <h4 className="text-xl font-black text-white tracking-tighter">{room.code}</h4>
                                                    {room.isActive && (
                                                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-3 text-xs font-bold text-slate-500 uppercase tracking-tighter">
                                                    <span className="flex items-center gap-1"><Music className="w-3 h-3" /> {room.songCount} songs</span>
                                                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(room.lastActivity).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <ChevronRight className={cn(
                                            "w-5 h-5 transition-all",
                                            selectedRoom?.id === room.id ? "text-primary-400 translate-x-1" : "text-slate-700 group-hover:text-slate-500"
                                        )} />
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>

                {/* Room Inspection View */}
                {selectedRoom && (
                    <div className="lg:col-span-2 animate-in fade-in slide-in-from-right-4 duration-500">
                        <Card className="border-white/10 bg-white/5 backdrop-blur-md h-full flex flex-col overflow-hidden">
                            <div className="p-6 border-b border-white/10 bg-black/20 flex items-center justify-between">
                                <div>
                                    <h3 className="text-2xl font-black text-white tracking-tighter flex items-center gap-3">
                                        <Users className="w-6 h-6 text-primary-400" />
                                        Room <span className="text-primary-400">{selectedRoom.code}</span>
                                    </h3>
                                    <p className="text-xs font-bold text-slate-500 uppercase tracking-[0.2em] mt-1">
                                        Queue Inspection
                                    </p>
                                </div>
                                <Button variant="ghost" size="sm" onClick={() => inspectRoom(null)} className="text-slate-400 hover:text-white">
                                    Close
                                </Button>
                            </div>

                            <div className="flex-1 overflow-y-auto p-6 space-y-4">
                                {loadingQueue ? (
                                    <div className="py-20 text-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto" />
                                    </div>
                                ) : roomQueue.length === 0 ? (
                                    <div className="py-20 text-center">
                                        <Music className="w-12 h-12 text-slate-800 mx-auto mb-4" />
                                        <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">Queue is Empty</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {roomQueue.map((item, index) => (
                                            <div
                                                key={item.id}
                                                className="flex items-center gap-4 p-4 rounded-2xl bg-black/20 border border-white/5 group hover:border-white/10 transition-all"
                                            >
                                                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center font-black text-xs text-slate-500">
                                                    {index + 1}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h5 className="font-bold text-white truncate">{item.songTitle}</h5>
                                                    <p className="text-xs font-bold text-slate-500 uppercase tracking-tighter truncate">{item.songArtist}</p>
                                                </div>
                                                <div className="text-right hidden sm:block">
                                                    <p className="text-[10px] font-black text-slate-600 uppercase tracking-widest">Added By</p>
                                                    <p className="text-xs font-bold text-primary-400">{item.addedBy}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="p-4 bg-black/40 border-t border-white/10 flex items-center justify-between">
                                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                                    Active since {formatDate(selectedRoom.createdAt)}
                                </div>
                                <Button variant="destructive" size="sm" className="text-[10px] font-black uppercase tracking-[0.2em] px-4 py-2">
                                    Terminate Room
                                </Button>
                            </div>
                        </Card>
                    </div>
                )}
            </div>
        </div>
    )
}

export default AdminRoomsTab
